import type { MouseEvent } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import RowOptionsMenu from '../../../../shared/components/RowOptionsMenu';
import TruncatedText from '../../../../shared/components/TruncatedText';
import ServiceBadge from '../ServiceBadge';
import {
  addOnTone,
  ClientAction,
  ClientFormStep,
  CLIENT_COLUMN_WIDTHS,
  COLUMN_WIDTH,
  DISPLAY_DATE_FORMAT,
  formatCost,
  formatStorage,
  rowOptionsFor,
  subscriptionLabelFrom,
  subscriptionTone,
} from '../../constants';
import type { ClientAddOn, ClientRecord, SubscriptionType } from '../../../../types/ClientManagementTypes';
import type { SubscriptionTier } from '../../../../enums/clientEnums';
import styles from './ClientTables.module.scss';

interface BuildColumnsOptions {
  expandedRowKeys: number[];
  onToggleExpand: (clientId: number) => void;
  onAction: (action: ClientAction, client: ClientRecord, anchor: HTMLElement | null) => void;
  onOpenClient: (client: ClientRecord, step: ClientFormStep) => void;
  subscriptionTypesById: Map<SubscriptionTier, SubscriptionType>;
}

// A cell with its own destination must not also trigger the row's.
const stopRowClick = (event: MouseEvent) => event.stopPropagation();

// The chevron lives inside the client cell, not an expand column of its own --
// hence `showExpandColumn: false` on the table.
export const buildColumns = ({
  expandedRowKeys,
  onToggleExpand,
  onAction,
  onOpenClient,
  subscriptionTypesById,
}: BuildColumnsOptions): ColumnsType<ClientRecord> => [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: COLUMN_WIDTH.id,
  },
  {
    title: 'Client',
    dataIndex: 'name',
    key: 'name',
    width: COLUMN_WIDTH.name,
    ellipsis: { showTitle: false },
    render: (value: ClientRecord['name'], record) => {
      const isExpanded = expandedRowKeys.includes(record.id);

      return (
        <span className={styles.clientCell}>
          {record.hasAddons && (
            <button
              type="button"
              className={styles.expandToggle}
              aria-label={`${isExpanded ? 'Hide' : 'Show'} add-ons for ${value}`}
              aria-expanded={isExpanded}
              onClick={(event) => {
                stopRowClick(event);
                onToggleExpand(record.id);
              }}
            >
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
            </button>
          )}
          <TruncatedText text={value} />
        </span>
      );
    },
  },
  {
    title: 'Admin',
    dataIndex: 'adminEmail',
    key: 'adminEmail',
    width: COLUMN_WIDTH.adminEmail,
    ellipsis: { showTitle: false },
    render: (value: ClientRecord['adminEmail']) => <TruncatedText text={value} />,
  },
  {
    title: 'Subscription',
    dataIndex: 'subscriptionTier',
    key: 'subscriptionTier',
    width: COLUMN_WIDTH.subscription,
    // The pill opens the Subscription step, not the profile the row opens.
    render: (tier: ClientRecord['subscriptionTier'], record) => (
      <button
        type="button"
        className={styles.subscriptionButton}
        aria-label={`Subscription details for ${record.name}`}
        onClick={(event) => {
          stopRowClick(event);
          onOpenClient(record, ClientFormStep.SUBSCRIPTION);
        }}
      >
        <ServiceBadge label={subscriptionLabelFrom(subscriptionTypesById, tier)} tone={subscriptionTone(tier)} />
      </button>
    ),
  },
  {
    title: 'Expiration',
    dataIndex: 'expiration',
    key: 'expiration',
    width: COLUMN_WIDTH.expiration,
    render: (value: ClientRecord['expiration']) => (value ? dayjs(value).format(DISPLAY_DATE_FORMAT) : '—'),
  },
  {
    title: 'Storage',
    key: 'storage',
    width: COLUMN_WIDTH.storage,
    render: (_, record) => formatStorage(record.storageUsedGb, record.storageTotalGb),
  },
  {
    title: 'Cost',
    dataIndex: 'cost',
    key: 'cost',
    width: COLUMN_WIDTH.cost,
    render: (value: ClientRecord['cost']) => formatCost(value),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: COLUMN_WIDTH.status,
  },
  {
    title: '',
    key: 'actions',
    width: COLUMN_WIDTH.actions,
    fixed: 'right',
    render: (_, record) => (
      <span onClick={stopRowClick}>
        <RowOptionsMenu
          options={rowOptionsFor(record.archived)}
          header="Client Options"
          onSelect={(actionId, trigger) => onAction(actionId as ClientAction, record, trigger)}
        />
      </span>
    ),
  },
];

type AddOnColumn = ColumnsType<ClientAddOn>[number];

// Content sits under the client columns above; every other slot is a spacer.
const ADD_ON_CONTENT_BY_POSITION: Record<number, AddOnColumn> = {
  1: {
    title: 'Add-On',
    dataIndex: 'name',
    key: 'name',
    render: (name: ClientAddOn['name'], record) => <ServiceBadge label={name} tone={addOnTone(record.addonType)} />,
  },
  2: {
    title: 'Expiration',
    dataIndex: 'expiration',
    key: 'expiration',
    render: (value: ClientAddOn['expiration']) => (value ? dayjs(value).format(DISPLAY_DATE_FORMAT) : '—'),
  },
  3: {
    title: 'Cost',
    dataIndex: 'cost',
    key: 'cost',
    render: (value: ClientAddOn['cost']) => formatCost(value),
  },
  4: {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
  },
};

// Built from the client widths above: both tables are table-layout:fixed, and
// mismatched colgroups misalign once the browser has slack to distribute.
export const addOnColumns: ColumnsType<ClientAddOn> = CLIENT_COLUMN_WIDTHS.map((width, position) => ({
  ...(ADD_ON_CONTENT_BY_POSITION[position] ?? { title: '', key: `spacer-${position}`, render: () => null }),
  width,
}));
